import {Inject, Injectable} from '@angular/core';
import {TmsCollector, TmsCollectorConfig, WindowObject} from '@spartacus/tracking/tms/core';
import {CxEvent, EventService} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DCP_EDDL_DATA_LAYER_SERVICE} from './tokens';
import {DcpDataService} from '../dcp-data.service';
import {isObservable} from 'rxjs';
import {filter, first, map, take} from 'rxjs/operators';
import {EddlInitializationEvent} from './events/eddl-initialization-event';
import {EddlMergedAbstract} from './events/eddl-merged.abstract';
import {DcpEddlDispatchService} from './dcp-eddl-dispatch-beacon.service';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {HttpClient} from '@angular/common/http';
import {FeatureTogglesService} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {EddlBeaconKey, EddlCollectionState} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {DpcEddlQueueService} from '@app/services/analytics/EDDL/dpc-eddl-queue.service';
import {DcpEddlSessionHelperService} from '@app/services/analytics/EDDL/dcp-eddl-session-helper.service';

export const EDDL_DATA_LAYER_NAME = 'adobeDataLayer';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpEddlCollectorService implements TmsCollector {

  private featureIsEnabled: boolean = false;

  constructor(
    @Inject(DCP_EDDL_DATA_LAYER_SERVICE) private dataServiceList: DcpDataService<DcpEddlCollectorService>[],
    private featureToggleService: FeatureTogglesService,
    private dispatchService: DcpEddlDispatchService,
    private dpcEddlQueueService: DpcEddlQueueService,
    private dcpEddlSessionHelperService: DcpEddlSessionHelperService,
    private events: EventService,
    private kymaService: DcpKymaService,
    private http: HttpClient,
  ) {
    this.featureToggleService.isEnabled(UNLEASH_TOGGLES.adobeEddlEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => this.featureIsEnabled = enabled);

    this.dpcEddlQueueService.getQueue$().pipe(
      untilDestroyed(this),
      map((q) =>
        q.filter((it) => it.event?.collectState === EddlCollectionState.FINISH)
          .map((it) => ({collectToken: it.event.collectToken}))
      ),
      filter((q) => q?.length > 0),
    ).subscribe((collectTokens) => {
      collectTokens.forEach(({collectToken}) => this.pushCollection(collectToken))
    })

  }

  init(config: TmsCollectorConfig, windowObject: WindowObject): void {
    windowObject[EDDL_DATA_LAYER_NAME] = windowObject[EDDL_DATA_LAYER_NAME] ?? [];
    setTimeout(() => {
      this.events.dispatch<EddlInitializationEvent>(new EddlInitializationEvent());
    });
  }

  pushEvent<T extends CxEvent>(config: TmsCollectorConfig, windowObject: WindowObject, event: T): void {
    this.triggerDataServices<T>(windowObject, event);
  }

  private pushCollection(collectToken: string) {
    let collectData = this.dpcEddlQueueService.getQueue();

    if (collectData.every((e) => e.event.collectOrder)) {
      collectData
        .sort((a, b) => a.event.collectOrder - b.event.collectOrder)
    }

    this.dpcEddlQueueService.getQueue()
      .filter((q) => q.event.collectToken === collectToken)
      .forEach(({wo, event, data}) =>
        this.updateDataLayer<EddlMergedAbstract>(wo, data, event, {skipBeacon: event.collectState !== EddlCollectionState.FINISH})
      )

  }

  protected triggerDataServices<T>(windowObject: WindowObject, event: T): void {
    this.dataServiceList.forEach((dataService) => {
      const data = dataService.getData(event);
      if (isObservable(data)) {
        data.pipe(first(), untilDestroyed(this)).subscribe((observableData) => {
          if ((event as any)?.collectToken && observableData) {
            this.dpcEddlQueueService.updateQueue({event, data: observableData, wo: windowObject});
          } else {
            this.updateDataLayer(windowObject, observableData, event);
          }
        });
      } else {
        if ((event as any)?.collectToken && data) {
          this.dpcEddlQueueService.updateQueue({event, data, wo: windowObject})
        } else {
          this.updateDataLayer(windowObject, data, event);
        }
      }
    });
  }

  protected updateDataLayer<T extends CxEvent & { manualBeacon?: EddlBeaconKey }>(
    windowObject: WindowObject,
    data: DcpEddlCollectorService,
    event: T,
    options?: {
      skipBeacon?: boolean,
      skipPush?: boolean,
      manually?: EddlBeaconKey
    }): void {
    if (data) {
      this.http.post<DcpEddlCollectorService>(
        this.kymaService.getUrl('eddlEvents'),
        data
      ).pipe(untilDestroyed(this), take(1)).subscribe();
      if (!this.featureIsEnabled) {
        return;
      }

      if (!options?.skipPush) {
        windowObject[EDDL_DATA_LAYER_NAME].push(data);
      }

      if (!options?.skipBeacon) {
        if (event.hasOwnProperty('manualBeacon')) {
          this.dispatchService.triggerBeaconEventManually(event?.manualBeacon)
        } else {
          this.dispatchService.triggerBeaconEvents(event);
        }
        if (event.hasOwnProperty('collectToken')) {
          this.dcpEddlSessionHelperService.clearSessionHelper((event as EddlMergedAbstract).collectToken);
          this.dpcEddlQueueService.clearQueue((event as EddlMergedAbstract).collectToken);
        }
      }
    }
  }
}
